/**
 * delete_template Tool - Delete a test template
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import type { JobClient } from '../clients/job-client.js';

export interface DeleteTemplateArgs {
  projectId: string;
  templateId: string;
}

export const deleteTemplateToolDefinition: Tool = {
  name: 'delete_template',
  description: `Delete a test template permanently.

This removes the template definition. Existing schedules using this template will fail on their next execution.
To keep the template but stop using it, simply stop referencing it in new jobs/schedules.`,
  inputSchema: {
    type: 'object',
    properties: {
      projectId: {
        type: 'string',
        description: 'Project ID (required).',
      },
      templateId: {
        type: 'string',
        description: 'Template ID to delete (required). Get from list_templates.',
      },
    },
    required: ['projectId', 'templateId'],
  },
};

export async function handleDeleteTemplate(
  args: DeleteTemplateArgs,
  jobClient: JobClient,
): Promise<{ content: Array<{ type: 'text'; text: string }>; isError?: boolean }> {
  try {
    await jobClient.deleteTemplate(args.projectId, args.templateId);

    return {
      content: [{
        type: 'text',
        text: `Template deleted successfully.

Template ID: ${args.templateId}

The template has been permanently removed. Any schedules referencing this template should be updated or deleted.`,
      }],
    };
  } catch (error) {
    const err = error as Error & { status?: number };
    return {
      content: [{
        type: 'text',
        text: `Failed to delete template\n\nError: ${err.message}${err.status ? `\nStatus: ${err.status}` : ''}`,
      }],
      isError: true,
    };
  }
}
